/**
 * The e-gift card redemption overlay — Rewards page "Redeem for E-Gift
 * Card" flow.
 *
 * Three steps, one direction:
 *   choose  — merchant + miles, against the LIVE ledger balance (the
 *             balance prop comes from useMilesBalanceRealtime, which reads
 *             the server's ledger sum — never a client-side number).
 *   confirm — the whole point of this screen is finality: the customer
 *             must acknowledge that submitting deducts the miles and locks
 *             the merchant + amount before the submit button arms.
 *   done    — receipt state pointing at the Trips page, where the service
 *             card (ServiceInquiryCard) tracks fulfillment.
 *
 * Submission POSTs /api/rewards/gift-card-redemptions; the server
 * re-validates everything against the ledger, so nothing here is a gate —
 * it's a good experience in front of one.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, CheckCircle2, Loader2, AlertTriangle, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { fmtCurrency } from '@/utils/money';
import { MILES_TRIP_BONUS_PERCENT, tripCentsForMiles } from '../../../supabase/functions/_shared/miles-redemption.js';
import {
  GIFT_CARD_MERCHANTS,
  GIFT_CARD_MIN_MILES,
  GIFT_CARD_DENOMINATION_MILES,
  isValidGiftCardMiles,
  giftCardValueUsd,
  maxRedeemableMiles,
  formatGiftCardUsd as usd,
} from '@/constants/giftCards';

const QUICK_MILES = [500, 1000, 1500, 2000, 2500, 5000, 7500, 10000];

// The catalog never changes at runtime — build the option vnodes once so
// typing in the miles input doesn't re-reconcile 200+ children per key.
const MERCHANT_OPTIONS = GIFT_CARD_MERCHANTS.map((name) => (
  <option key={name} value={name}>{name}</option>
));

export default function GiftCardRedeemModal({
  open,
  onClose,
  balance = 0,
  isBalanceLoading = false,
  userEmail = '',
  initialMerchant = '',
  initialMiles = '',
  signedOut = false,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose'); // 'choose' | 'confirm' | 'done'
  const [merchant, setMerchant] = useState('');
  const [milesInput, setMilesInput] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const submitLockRef = useRef(false);

  // Re-seed from the calculator every time the overlay opens.
  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setMerchant(initialMerchant || '');
    setMilesInput(initialMiles ? String(initialMiles) : '');
    setAcknowledged(false);
    setSubmitting(false);
    setError('');
    submitLockRef.current = false;
  }, [open, initialMerchant, initialMiles]);

  const miles = parseInt(milesInput, 10) || 0;
  const maxMiles = maxRedeemableMiles(balance);
  const validAmount = isValidGiftCardMiles(miles) && miles <= balance;
  const canContinue = Boolean(merchant) && validAmount && !isBalanceLoading;

  const amountHint = useMemo(() => {
    if (!miles) return '';
    if (miles > balance) {
      return `You have ${Math.floor(balance).toLocaleString()} miles available.`;
    }
    if (!isValidGiftCardMiles(miles)) {
      return `Amounts start at ${GIFT_CARD_MIN_MILES.toLocaleString()} miles and step in ${GIFT_CARD_DENOMINATION_MILES.toLocaleString()}-mile ($${giftCardValueUsd(GIFT_CARD_DENOMINATION_MILES)}) increments.`;
    }
    return '';
  }, [miles, balance]);

  const close = () => {
    if (submitting) return;
    onClose?.();
  };

  const submit = async () => {
    // Synchronous re-entry guard: two clicks in the same frame both read
    // submitting === false (state flushes on the NEXT render), so the
    // state alone can't stop a double POST — the ref can.
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) {
        setError('Your session expired — please sign in again.');
        setSubmitting(false);
        submitLockRef.current = false;
        return;
      }
      const res = await fetch('/api/rewards/gift-card-redemptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ merchant, miles }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.ok) {
        setError(body?.error || 'Couldn\'t submit your redemption. Please try again.');
        setSubmitting(false);
        submitLockRef.current = false;
        return;
      }
      setSubmitting(false);
      setStep('done');
    } catch {
      setError('Couldn\'t reach Defera. Check your connection and try again.');
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Redeem for E-Gift Card"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-brand-800/40 bg-gradient-to-b from-brand-950 to-[#070b18] shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-white/[0.06]">
              <div className="w-9 h-9 rounded-xl bg-brand-900/50 border border-brand-800/40 flex items-center justify-center shrink-0">
                <Gift className="w-[18px] h-[18px] text-brand-200/90" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <h2 className="text-white text-base font-medium leading-tight">
                  {step === 'done' ? 'Request submitted' : 'Redeem for E-Gift Card'}
                </h2>
                <p className="text-white/60 text-xs font-normal">
                  {step === 'done'
                    ? 'Your e-gift card is on its way'
                    : 'Delivered electronically'}
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                aria-label="Close"
                className="ml-auto text-white/40 hover:text-white/80 transition-colors disabled:opacity-40"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>

            {step === 'choose' && signedOut && (
              <div className="px-6 py-7 text-center space-y-4">
                <p className="text-white/85 text-sm font-normal leading-relaxed">
                  Sign in to your Defera account to redeem your miles for e-gift cards at 200+ merchants.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="w-full py-3 rounded-xl text-sm font-normal text-white/70 border border-brand-800/40 bg-brand-950/50 hover:text-white transition-colors"
                >
                  Close
                </button>
              </div>
            )}

            {step === 'choose' && !signedOut && (
              <div className="px-6 py-5 space-y-5">
                {/* Live balance — the ledger's number, not the page's */}
                <div className="rounded-2xl border border-brand-800/35 bg-brand-900/25 px-4 py-3 flex items-baseline justify-between">
                  <span className="text-white/50 text-xs font-normal">Available to redeem</span>
                  <span className="text-brand-200/95 text-sm font-medium tabular-nums">
                    {isBalanceLoading ? '—' : `${Math.floor(balance).toLocaleString()} miles · up to $${usd(maxMiles)}`}
                  </span>
                </div>

                <div>
                  <label htmlFor="redeem-merchant" className="block text-xs font-normal text-white/60 mb-1.5">
                    Merchant <span className="text-white/30">*</span>
                  </label>
                  <select
                    id="redeem-merchant"
                    value={merchant}
                    onChange={(e) => setMerchant(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-brand-800/40 bg-brand-950/60 text-white font-normal text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                  >
                    <option value="">Select a merchant</option>
                    {MERCHANT_OPTIONS}
                  </select>
                </div>

                <div>
                  <label htmlFor="redeem-miles" className="block text-xs font-normal text-white/60 mb-1.5">
                    Miles <span className="text-white/30">*</span>
                  </label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {QUICK_MILES.filter((m) => m <= maxMiles).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMilesInput(String(m))}
                        className={`px-2.5 py-1 rounded-lg border text-xs font-normal transition-colors ${
                          miles === m
                            ? 'border-brand-400/60 bg-brand-800/50 text-white'
                            : 'border-brand-800/40 bg-brand-950/50 text-white/60 hover:text-white/90'
                        }`}
                      >
                        ${m / 100}
                      </button>
                    ))}
                    {maxMiles >= GIFT_CARD_MIN_MILES && (
                      <button
                        type="button"
                        onClick={() => setMilesInput(String(maxMiles))}
                        className="px-2.5 py-1 rounded-lg border border-brand-800/40 bg-brand-950/50 text-brand-300/90 text-xs font-normal hover:text-brand-200"
                      >
                        Max
                      </button>
                    )}
                  </div>
                  <input
                    id="redeem-miles"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step={GIFT_CARD_DENOMINATION_MILES}
                    value={milesInput}
                    onChange={(e) => setMilesInput(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder={`${GIFT_CARD_MIN_MILES.toLocaleString()} miles minimum`}
                    className="w-full px-4 py-3 rounded-xl border border-brand-800/40 bg-brand-950/60 text-white font-normal text-base focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                  />
                  {amountHint && (
                    <p className="mt-1.5 text-[11.5px] text-amber-300/80 font-normal">{amountHint}</p>
                  )}
                </div>

                <div className="pt-3 border-t border-white/[0.06] space-y-2.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-white/50 text-xs font-normal">E-gift card value</span>
                    <span className="text-white text-xl font-light tabular-nums">${usd(miles)}</span>
                  </div>
                  {/* The same Miles go further on a Defera trip; say so where
                      the choice is being made, with the way to take it. */}
                  {miles > 0 && (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200/15 bg-amber-300/[0.06] px-3 py-2.5">
                      <p className="text-[12px] font-normal text-amber-50/85 leading-snug">
                        On a Defera trip, these Miles take{' '}
                        <span className="font-medium text-white tabular-nums">${fmtCurrency(tripCentsForMiles(miles))}</span>{' '}
                        off the price ({MILES_TRIP_BONUS_PERCENT}% more).
                      </p>
                      <button
                        type="button"
                        onClick={() => { onClose?.(); navigate('/search'); }}
                        className="shrink-0 text-[12px] font-medium text-white underline underline-offset-2 decoration-white/30 hover:decoration-white transition-colors"
                      >
                        Use on a trip
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setStep('confirm')}
                  disabled={!canContinue}
                  className="w-full py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-b from-brand-700 to-brand-800 hover:from-brand-600 hover:to-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Continue
                </button>
              </div>
            )}

            {step === 'confirm' && (
              <div className="px-6 py-5 space-y-5">
                <div className="rounded-2xl border border-brand-800/35 bg-brand-900/25 divide-y divide-white/[0.05]">
                  <div className="px-4 py-3 flex justify-between gap-4">
                    <span className="text-white/50 text-xs font-normal">Merchant</span>
                    <span className="text-white text-sm font-normal text-right">{merchant}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between gap-4">
                    <span className="text-white/50 text-xs font-normal">Miles</span>
                    <span className="text-white text-sm font-normal tabular-nums">{miles.toLocaleString()}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between gap-4">
                    <span className="text-white/50 text-xs font-normal">E-gift card value</span>
                    <span className="text-white text-sm font-medium tabular-nums">${usd(miles)}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between gap-4">
                    <span className="text-white/50 text-xs font-normal">Delivery</span>
                    <span className="text-white/80 text-sm font-normal text-right break-all">
                      Electronically{userEmail ? ` · ${userEmail}` : ''}
                    </span>
                  </div>
                  <div className="px-4 py-3 flex justify-between gap-4">
                    <span className="text-white/50 text-xs font-normal">Arrives</span>
                    <span className="text-white/80 text-sm font-normal text-right">3–5 business days</span>
                  </div>
                </div>

                <div className="rounded-2xl border border-amber-400/20 bg-gradient-to-b from-amber-500/[0.10] to-amber-500/[0.015] overflow-hidden">
                  <div className="flex gap-3 px-4 pt-4 pb-3.5">
                    <div className="w-8 h-8 rounded-lg bg-amber-400/15 border border-amber-400/20 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-4 h-4 text-amber-300" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-amber-50 text-[13px] font-medium leading-snug">This choice is final</p>
                      <p className="mt-1 text-amber-100/65 text-[12px] font-normal leading-relaxed">
                        Your miles are deducted the moment you submit. The merchant and amount can&apos;t be
                        changed or swapped afterward.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={acknowledged}
                    onClick={() => setAcknowledged((v) => !v)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 border-t text-left transition-colors ${
                      acknowledged
                        ? 'border-amber-400/25 bg-amber-400/[0.10]'
                        : 'border-amber-400/15 hover:bg-amber-400/[0.05]'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-all duration-150 ${
                        acknowledged ? 'bg-amber-400 border border-amber-400' : 'border border-amber-300/40'
                      }`}
                    >
                      <Check
                        className={`w-3.5 h-3.5 text-brand-950 transition-opacity ${acknowledged ? 'opacity-100' : 'opacity-0'}`}
                        strokeWidth={3}
                      />
                    </span>
                    <span className="text-[12.5px] text-white/85 font-normal leading-snug">
                      I understand my merchant choice and amount are final.
                    </span>
                  </button>
                </div>

                {error && (
                  <p className="text-[12.5px] text-red-300/90 font-normal" role="alert">{error}</p>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setStep('choose'); setAcknowledged(false); setError(''); }}
                    disabled={submitting}
                    className="flex-1 py-3 rounded-xl text-sm font-normal text-white/70 border border-brand-800/40 bg-brand-950/50 hover:text-white transition-colors disabled:opacity-40"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={!acknowledged || submitting}
                    className="flex-1 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-b from-brand-700 to-brand-800 hover:from-brand-600 hover:to-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
                    {submitting ? 'Submitting…' : 'Confirm & Redeem'}
                  </button>
                </div>
              </div>
            )}

            {step === 'done' && (
              <div className="px-6 py-7 text-center space-y-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-300/90 mx-auto" strokeWidth={1.5} />
                <div>
                  <p className="text-white text-sm font-normal leading-relaxed">
                    Your ${usd(miles)} {merchant} e-gift card request is with our team, and the miles
                    have been deducted from your balance.
                  </p>
                  <p className="mt-2 text-white/60 text-[12.5px] font-normal leading-relaxed">
                    Track it on your Trips page — your e-gift card arrives by email within 3–5 business
                    days. The team only reaches out if they hit a snag.
                  </p>
                </div>
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={close}
                    className="flex-1 py-3 rounded-xl text-sm font-normal text-white/70 border border-brand-800/40 bg-brand-950/50 hover:text-white transition-colors"
                  >
                    Done
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/trips')}
                    className="flex-1 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-b from-brand-700 to-brand-800 hover:from-brand-600 hover:to-brand-700 transition-colors"
                  >
                    View in Trips
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
